/**
 * Linhas de evento reutilizadas pelo calendário (mês/semana/dia/lista) e dialogs.
 *
 * Adendo 3 da 039: o compromisso de período diz o período ("Tarde ABC1D23"),
 * e não a hora da janela, nas duas abas — `commitmentTimeLabel` e
 * `commitmentTimeRangeLabel` do @erp/shared. A semana e o dia daqui são listas
 * por dia, não grades por hora: a janela entra pela ordem (manhã às 08:00,
 * tarde às 13:00) e o rótulo é o período.
 */
import {
  COMMITMENT_SITUATION_LABELS,
  commitmentTimeLabel,
  commitmentTimeRangeLabel,
} from '@erp/shared';
import type { AgendaEventItem } from './agenda-format';
import { ABSENCE_COLOR, attendantColor, commitmentColor, eventKey } from './agenda-format';
import { SITUATION_STYLE, SituationIcon, commitmentLabel, situationOf } from './commitment-situation';
import { HIDDEN_TICKET_LABEL, HiddenTicketIcon } from './hidden-ticket-mark';

/**
 * `general` = a Agenda de sempre; `plates` = a aba de placas (spec 039): o
 * compromisso vira `HH:MM PLACA` com a marca da situação, e a cor é a dela.
 * Com o switch de ocultos (adendo), o de ticket oculto leva também a marca
 * "Oculto no ERP" — só a aba de placas os pede.
 */
export type AgendaRowMode = 'general' | 'plates';

/** Linha compacta da célula do mês (12px, uma linha, truncada). */
export function MonthEventRow({
  item,
  onClick,
  mode = 'general',
}: {
  item: AgendaEventItem;
  onClick: (item: AgendaEventItem) => void;
  mode?: AgendaRowMode;
}) {
  if (item.kind === 'appointment') {
    const a = item.appointment;
    return (
      <button
        type="button"
        onClick={() => onClick(item)}
        className="flex w-full min-w-0 cursor-pointer items-center gap-1 rounded-sm px-1 py-px text-left text-xs leading-4 hover:bg-muted"
        title={`${a.startTime} ${a.ticketTitle}`}
      >
        <span
          className="size-2 shrink-0 rounded-full"
          style={{ background: attendantColor(a.userId) }}
          aria-hidden
        />
        <span className="truncate">
          <span className="tabular-nums">{a.startTime}</span> {a.ticketTitle}
        </span>
      </button>
    );
  }
  if (item.kind === 'commitment' && mode === 'plates') {
    const c = item.commitment;
    const situation = situationOf(c);
    const label = commitmentLabel(c);
    return (
      <button
        type="button"
        onClick={() => onClick(item)}
        className="flex w-full min-w-0 cursor-pointer items-center gap-1 rounded-sm px-1 py-px text-left text-xs leading-4 hover:opacity-80"
        style={{ background: `${SITUATION_STYLE[situation].color}1F` }}
        title={`${commitmentTimeLabel(c)} ${label} · ${COMMITMENT_SITUATION_LABELS[situation]}${
          c.ticketHidden ? ` · ${HIDDEN_TICKET_LABEL}` : ''
        }`}
      >
        <SituationIcon situation={situation} className="size-3" />
        <span className="truncate">
          <span className="tabular-nums">{commitmentTimeLabel(c)}</span> <span className="font-medium">{label}</span>
        </span>
        {c.ticketHidden ? <HiddenTicketIcon className="size-3" /> : null}
      </button>
    );
  }
  if (item.kind === 'commitment') {
    const c = item.commitment;
    return (
      <button
        type="button"
        onClick={() => onClick(item)}
        className="block w-full min-w-0 cursor-pointer truncate rounded-sm px-1 py-px text-left text-xs leading-4 text-white hover:opacity-90"
        style={{ background: commitmentColor(c) }}
        title={`${commitmentTimeLabel(c)} ${c.ticketTitle}`}
      >
        <span className="tabular-nums">{commitmentTimeLabel(c)}</span> {c.ticketTitle}
      </button>
    );
  }
  const ab = item.absence;
  return (
    <button
      type="button"
      onClick={() => onClick(item)}
      className="block w-full min-w-0 cursor-pointer truncate rounded-sm px-1 py-px text-left text-xs leading-4 text-white hover:opacity-90"
      style={{ background: ABSENCE_COLOR }}
      title={`Ausência – ${ab.userName}`}
    >
      Ausência – {ab.userName}
    </button>
  );
}

/** Linha completa (semana/dia/lista e dialog "mais +N"). */
export function FullEventRow({
  item,
  onClick,
  mode = 'general',
}: {
  item: AgendaEventItem;
  onClick: (item: AgendaEventItem) => void;
  mode?: AgendaRowMode;
}) {
  if (item.kind === 'appointment') {
    const a = item.appointment;
    return (
      <button
        type="button"
        onClick={() => onClick(item)}
        className="flex w-full min-w-0 cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
      >
        <span
          className="size-2.5 shrink-0 rounded-full"
          style={{ background: attendantColor(a.userId) }}
          aria-hidden
        />
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
          {a.startTime}
          {a.endTime ? ` - ${a.endTime}` : ''}
        </span>
        <span className="min-w-0 flex-1 truncate">
          #{a.ticketNumber} - {a.ticketTitle}
        </span>
        <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
          {a.userName}
        </span>
      </button>
    );
  }
  if (item.kind === 'commitment' && mode === 'plates') {
    const c = item.commitment;
    const situation = situationOf(c);
    return (
      <button
        type="button"
        onClick={() => onClick(item)}
        className="flex w-full min-w-0 cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:opacity-80"
        style={{ background: `${SITUATION_STYLE[situation].color}14` }}
      >
        <SituationIcon situation={situation} className="size-4" />
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
          {commitmentTimeRangeLabel(c)}
        </span>
        <span className="min-w-0 flex-1 truncate">
          <span className="font-medium">{commitmentLabel(c)}</span>
          <span className="text-muted-foreground">
            {' '}
            #{c.ticketNumber}
            {c.assigneeName ? ` · ${c.assigneeName}` : c.calendarName ? ` · ${c.calendarName}` : ''}
          </span>
        </span>
        {c.ticketHidden ? <HiddenTicketIcon className="size-4" /> : null}
        <span
          className="hidden shrink-0 text-xs font-medium sm:inline"
          style={{ color: SITUATION_STYLE[situation].color }}
        >
          {COMMITMENT_SITUATION_LABELS[situation]}
        </span>
      </button>
    );
  }
  if (item.kind === 'commitment') {
    const c = item.commitment;
    return (
      <button
        type="button"
        onClick={() => onClick(item)}
        className="flex w-full min-w-0 cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-white hover:opacity-90"
        style={{ background: commitmentColor(c) }}
      >
        <span className="shrink-0 text-xs tabular-nums opacity-90">
          {commitmentTimeRangeLabel(c)}
        </span>
        <span className="min-w-0 flex-1 truncate">
          #{c.ticketNumber} - {c.ticketTitle}
        </span>
      </button>
    );
  }
  const ab = item.absence;
  return (
    <button
      type="button"
      onClick={() => onClick(item)}
      className="flex w-full min-w-0 cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-white hover:opacity-90"
      style={{ background: ABSENCE_COLOR }}
    >
      <span className="min-w-0 flex-1 truncate">Ausência – {ab.userName}</span>
    </button>
  );
}

export { eventKey };
